import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  SaveApiResMessage,
  createScrapeSettingsAgenda,
  deleteScrapeSettingsAgenda,
  getScrapeSettingsAgenda,
  updateScrapeSettingsAgenda,
  updateScrapeSettingsAgendaStatus,
} from '@/actions';
import LoadingV2 from '@/components/Loading';
import { TableV3 } from '@/components/TableV3';
import PageContainerAB from '@/components/PageContainerAB/pageContainer';
import Modal from '@/ctComponents/Modal';
import CustomSwitchV2 from '@/components/CostomSwitch/CustomSwitchV2';
import { Input } from '@/components/Input';
import CustomSelectEx from '@/ctComponents/CustomSelectEx';
import TimePickerV2 from '@/components/TimePicker/TimePickerV2';
import IconButton from '@/ctComponents/IconButton';
import CloseButton from '@/ctComponents/closeButton';
import SubmitButton from '@/ctComponents/submitButton';
import IconButtonV2 from '@/ctComponents/IconButton/IconButtonV2';
import DeletePopup from '@/components/DeletePopup';

const getScheduleTypeLabel = (item) => {
  const typeValue =
    item?.type || item?.schedule_type || item?.scheduler_type || item?.mode;

  if (!typeValue) return '-';

  if (String(typeValue).toLowerCase().includes('boost')) return 'Boost';
  if (String(typeValue).toLowerCase().includes('continuous'))
    return 'Continuous';

  return typeValue;
};

const getWeekDaysLabel = (item) => {
  if (item?.days_label) return item.days_label;

  const dayMap = [
    ['mon', 'Mon'],
    ['tue', 'Tue'],
    ['wed', 'Wed'],
    ['thu', 'Thu'],
    ['fri', 'Fri'],
    ['sat', 'Sat'],
    ['sun', 'Sun'],
  ];
  const enabledDays = dayMap
    .filter(([key]) => Number(item?.[key]) === 1)
    .map(([, label]) => label);

  if (!enabledDays.length) return '-';
  if (
    enabledDays.length === 5 &&
    enabledDays.join(',') === 'Mon,Tue,Wed,Thu,Fri'
  ) {
    return 'Mon-Fri';
  }

  return enabledDays.join(', ');
};

export default function ScrapeSettingsAgenda() {
  const location = useLocation();
  const { scrapeSettingId, terminalName: terminalNameFromUrl } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [terminalName, setTerminalName] = useState('');
  const [priorities, setPriorities] = useState(['HIGH', 'MEDIUM', 'LOW']);
  const [types, setTypes] = useState(['CONTINUES', 'BOOST']);
  const [statusOptions, setStatusOptions] = useState(['ACTIVE', 'INACTIVE']);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    show: false,
    agendaId: null,
    loading: false,
  });
  const [form, setForm] = useState({
    name: '',
    priority: '',
    type: 'CONTINUES',
    time_start: '',
    time_end: '',
    frequency: '',
    status: 'ACTIVE',
    offset_backward: '',
    offset_forward: '',
    day_sat: false,
    day_sun: false,
    day_mon: false,
    day_tue: false,
    day_wed: false,
    day_thu: false,
    day_fri: false,
  });
  const dayOptions = [
    { label: 'Mon', value: 'day_mon' },
    { label: 'Tue', value: 'day_tue' },
    { label: 'Wed', value: 'day_wed' },
    { label: 'Thu', value: 'day_thu' },
    { label: 'Fri', value: 'day_fri' },
    { label: 'Sat', value: 'day_sat' },
    { label: 'Sun', value: 'day_sun' },
  ];
  const routeRowData = location?.state?.scrapeSettingRow || null;

  const titleTerminalName =
    terminalNameFromUrl ||
    terminalName ||
    location?.state?.terminalName ||
    routeRowData?.terminal?.slug ||
    routeRowData?.scrapeable_id ||
    '';
  const pageTitle = useMemo(() => {
    return `Existing Schedules (${titleTerminalName || scrapeSettingId})`;
  }, [titleTerminalName, scrapeSettingId]);

  // همیشه موقع ورود به این صفحه، اسکرول رو بالا بیار
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, []);

  const statusActiveValue =
    statusOptions?.includes('ACTIVE')
      ? 'ACTIVE'
      : statusOptions?.[0] || 'ACTIVE';
  const statusInactiveValue =
    statusOptions?.includes('INACTIVE')
      ? 'INACTIVE'
      : statusOptions?.[1] || 'INACTIVE';

  const loadAgenda = () => {
    setLoading(true);
    getScrapeSettingsAgenda(scrapeSettingId)
      .then((res) => {
        const rows = res?.data?.schedulers || [];
        setPriorities(
          Array.isArray(res?.data?.priorities) &&
            res?.data?.priorities.length > 0
            ? res.data.priorities
            : ['HIGH', 'MEDIUM', 'LOW']
        );
        setTypes(
          Array.isArray(res?.data?.types) && res?.data?.types.length > 0
            ? res.data.types
            : ['CONTINUES', 'BOOST']
        );
        setStatusOptions(
          Array.isArray(res?.data?.status) && res?.data?.status.length > 0
            ? res.data.status
            : ['ACTIVE', 'INACTIVE']
        );
        const firstRow =
          Array.isArray(rows) && rows.length > 0 ? rows[0] : null;
        const terminalFromResponse =
          res?.data?.terminal?.slug ||
          res?.data?.terminal?.name ||
          res?.data?.scrape_setting?.terminal?.slug ||
          res?.data?.scrape_setting?.terminal?.name ||
          firstRow?.terminal ||
          firstRow?.terminal_slug ||
          firstRow?.scrapeable_id ||
          '';
        setTerminalName(terminalFromResponse);
        setSchedules(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        SaveApiResMessage({
          title: 'Error',
          message: err,
          mode: 'warning',
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const resetForm = () => {
    setForm({
      name: '',
      priority: '',
      type: 'CONTINUES',
      time_start: '',
      time_end: '',
      frequency: '',
      status: statusActiveValue,
      offset_backward: '',
      offset_forward: '',
      day_sat: false,
      day_sun: false,
      day_mon: false,
      day_tue: false,
      day_wed: false,
      day_thu: false,
      day_fri: false,
    });
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEdit = (row) => {
    const backward = Number(row?.offset_backward || 0);
    const forward = Number(row?.offset_forward || 0);

    setEditingId(row?.id);
    setForm({
      name: row?.name || '',
      priority: row?.priority || priorities?.[0] || 'HIGH',
      type: row?.type || 'CONTINUES',
      time_start: row?.time_start || '',
      time_end: row?.time_end || '',
      frequency: row?.frequency ?? '',
      status: row?.status || statusActiveValue,
      offset_backward:
        row?.offset_backward === null || row?.offset_backward === undefined
          ? ''
          : String(row?.offset_backward),
      offset_forward:
        row?.offset_forward === null || row?.offset_forward === undefined
          ? ''
          : String(row?.offset_forward),
      day_sat: !!row?.day_sat,
      day_sun: !!row?.day_sun,
      day_mon: !!row?.day_mon,
      day_tue: !!row?.day_tue,
      day_wed: !!row?.day_wed,
      day_thu: !!row?.day_thu,
      day_fri: !!row?.day_fri,
    });
    setFormOpen(true);
  };

  const validateForm = () => {
    const trimmedName = form.name?.trim();
    if (!trimmedName) return 'Scheduler name is required.';
    if (trimmedName.length > 255) {
      return 'Scheduler name must be between 1 and 255 characters.';
    }
    if (!form.priority) return 'Priority is required.';
    if (!form.type) return 'Type is required.';
    if (!form.time_start) return 'Start time is required.';
    if (form.type === 'CONTINUES' && !form.time_end) {
      return 'End time is required for CONTINUES.';
    }
    if (
      form.type === 'CONTINUES' &&
      (form.frequency === '' || Number(form.frequency) <= 0)
    ) {
      return 'Frequency is required for CONTINUES.';
    }
    if (form.type === 'CONTINUES' && !Number.isInteger(Number(form.frequency))) {
      return 'Frequency must be an integer.';
    }
    if (
      form.offset_backward !== '' &&
      (!Number.isInteger(Number(form.offset_backward)) ||
        Number(form.offset_backward) < 0)
    ) {
      return 'Backward Offset must be a non-negative integer.';
    }
    if (
      form.offset_forward !== '' &&
      (!Number.isInteger(Number(form.offset_forward)) ||
        Number(form.offset_forward) < 0)
    ) {
      return 'Forward Offset must be a non-negative integer.';
    }
    return null;
  };

  const makePayload = () => {
    const offsetBackward =
      form.offset_backward === '' ? null : Number(form.offset_backward);
    const offsetForward =
      form.offset_forward === '' ? null : Number(form.offset_forward);

    const payload = {
      name: form.name?.trim(),
      priority: form.priority,
      type: form.type,
      time_start: form.time_start,
      offset_backward: offsetBackward,
      offset_forward: offsetForward,
      day_sat: form.day_sat ? true : null,
      day_sun: form.day_sun ? true : null,
      day_mon: form.day_mon ? true : null,
      day_tue: form.day_tue ? true : null,
      day_wed: form.day_wed ? true : null,
      day_thu: form.day_thu ? true : null,
      day_fri: form.day_fri ? true : null,
    };

    if (form.type === 'CONTINUES') {
      payload.time_end = form.time_end;
      payload.frequency = Number(form.frequency);
    }

    if (!editingId) {
      payload.scrape_setting_id = scrapeSettingId;
    }

    return payload;
  };

  const submitForm = () => {
    const error = validateForm();
    if (error) {
      SaveApiResMessage({ title: 'Warning', message: error, mode: 'warning' });
      return;
    }

    setSaving(true);
    const req = editingId
      ? updateScrapeSettingsAgenda(editingId, makePayload())
      : createScrapeSettingsAgenda(makePayload());

    req
      .then((res) => {
        SaveApiResMessage({
          title: res?.data?.messageHeader || 'Success',
          message: editingId
            ? 'Schedule updated successfully.'
            : 'Schedule created successfully.',
          mode: 'success',
        });
        setFormOpen(false);
        resetForm();
        loadAgenda();
      })
      .catch((err) => {
        SaveApiResMessage({ title: 'Error', message: err, mode: 'warning' });
      })
      .finally(() => setSaving(false));
  };

  const selectedDaysValue = dayOptions.filter((day) => form[day.value]);
  const priorityOptions = priorities.map((priority) => ({
    label: priority,
    value: priority,
  }));
  const typeOptions = types.map((type) => ({
    label: type === 'CONTINUES' ? 'Continuous Monitoring' : 'Boost Check',
    value: type,
  }));
  const offsetPreview = (() => {
    const back = Number(form.offset_backward || 0);
    const forward = Number(form.offset_forward || 0);
    if (!back && !forward) return '-';
    if (back && !forward) return `-${back} min`;
    if (!back && forward) return `+${forward} min`;
    return `-${back} / +${forward} min`;
  })();

  const performDelete = async (agendaId) => {
    if (!agendaId) return;
    setDeleteConfirm((p) => ({ ...p, loading: true }));
    try {
      const res = await deleteScrapeSettingsAgenda(agendaId);
      SaveApiResMessage({
        title: res?.data?.messageHeader || 'Success',
        message: 'Schedule deleted successfully.',
        mode: 'success',
      });
      // حذف لوکال ردیف از جدول، بدون reload کامل
      setSchedules((prev) =>
        Array.isArray(prev) ? prev.filter((it) => it?.id !== agendaId) : prev
      );
      setDeleteConfirm({ show: false, agendaId: null, loading: false });
    } catch (err) {
      SaveApiResMessage({ title: 'Error', message: err, mode: 'warning' });
      setDeleteConfirm({ show: false, agendaId: null, loading: false });
    }
  };

  const requestDelete = (agendaId) => {
    setDeleteConfirm({ show: true, agendaId, loading: false });
  };

  const toggleStatus = (row) => {
    const nextStatus =
      row?.status === statusActiveValue
        ? statusInactiveValue
        : statusActiveValue;
    updateScrapeSettingsAgendaStatus(row?.id, nextStatus)
      .then((res) => {
        SaveApiResMessage({
          title: res?.data?.messageHeader || 'Success',
          message: 'Status updated successfully.',
          mode: 'success',
        });
        // Update local table data immediately (no reload needed).
        setSchedules((prev) =>
          Array.isArray(prev)
            ? prev.map((it) =>
                it?.id === row?.id ? { ...it, status: nextStatus } : it
              )
            : prev
        );
      })
      .catch((err) => {
        SaveApiResMessage({ title: 'Error', message: err, mode: 'warning' });
      });
  };

  const getDaysLabel = (row) => {
    const days = getWeekDaysLabel({
      ...row,
      mon: row?.day_mon ?? row?.mon,
      tue: row?.day_tue ?? row?.tue,
      wed: row?.day_wed ?? row?.wed,
      thu: row?.day_thu ?? row?.thu,
      fri: row?.day_fri ?? row?.fri,
      sat: row?.day_sat ?? row?.sat,
      sun: row?.day_sun ?? row?.sun,
    });
    return days;
  };

  const getDetailLabel = (row) => {
    if (row?.type === 'BOOST') {
      return `${row?.time_start || '-'} Exact check`;
    }
    return `${row?.time_start || '-'} - ${row?.time_end || '-'} ${
      row?.frequency ? `Every ${row.frequency} min` : ''
    }`.trim();
  };

  const getOffsetLabel = (row) => {
    const back = Number(row?.offset_backward || 0);
    const forward = Number(row?.offset_forward || 0);
    if (!back && !forward) return '-';
    // Keep a deterministic sortValue label.
    return `back:${back},forward:${forward}`;
  };

  const getBackwardOffsetLabel = (row) => {
    const back = row?.offset_backward;
    if (back === null || back === undefined || back === 0) return '-';
    return `-${Number(back)} min`;
  };

  const getForwardOffsetLabel = (row) => {
    const forward = row?.offset_forward;
    if (forward === null || forward === undefined || forward === 0) return '-';
    return `+${Number(forward)} min`;
  };

  useEffect(() => {
    if (scrapeSettingId) {
      loadAgenda();
    }
  }, [scrapeSettingId]);

  const columns = [
    {
      field: 'name',
      label: 'Schedule Name',
      align: 'center',
      flex: 3,
      component: (row) => <div>{row?.name || '-'}</div>,
      sortValue: (row) => row?.name || '',
    },
    {
      field: 'type',
      label: 'Type',
      align: 'center',
      flex: 2,
      component: (row) => {
        const type = getScheduleTypeLabel(row);
        const isBoost = String(type).toLowerCase().includes('boost');
        return (
          <span
            className={`px-3 py-1 rounded-full text-xs ${
              isBoost
                ? 'bg-warning-light text-warning'
                : 'bg-primary-blue-light text-primary-blue'
            }`}>
            {type}
          </span>
        );
      },
      sortValue: (row) => getScheduleTypeLabel(row) || '',
    },
    {
      field: 'days',
      label: 'Days',
      align: 'center',
      flex: 2,
      minWidth: '145px',
      component: (row) => <div>{getDaysLabel(row)}</div>,
      sortValue: (row) => getDaysLabel(row) || '',
    },
    {
      field: 'detail',
      label: 'Detail',
      align: 'center',
      flex: 6,
      minWidth: '145px',
      component: (row) => <div>{getDetailLabel(row)}</div>,
      sortValue: (row) => getDetailLabel(row) || '',
    },
    {
      field: 'priority',
      label: 'Priority',
      align: 'center',
      flex: 1.5,
      component: (row) => {
        const p = row?.priority;
        if (!p) return <div>-</div>;

        const map = {
          HIGH: { label: 'High', bg: '#E0FAEC', text: '#1F7A44' }, // green
          MEDIUM: { label: 'Medium', bg: '#FFF1EB', text: '#C24C14' }, // orange/yellow-ish
          LOW: { label: 'Low', bg: '#FFFAEB', text: '#A06A00' }, // yellow-ish
        };

        const cfg = map[p] || { label: String(p), bg: '#F5F7FA', text: '#131517' };

        return (
          <span
            className="px-3 py-1 rounded-full text-xs font-averta font-bold"
            style={{
              background: cfg.bg,
              color: cfg.text,
              border: '1px solid transparent',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 56,
            }}>
            {cfg.label}
          </span>
        );
      },
      sortValue: (row) => row?.priority ?? '',
    },
    {
      field: 'offset_backward',
      label: 'Backward Offset',
      align: 'center',
      flex: 0.3,
      minWidth: '70px',
      maxWidth: '70px',
      component: (row) => (
        <div className="font-averta text-xs font-bold text-primary-text">
          {getBackwardOffsetLabel(row)}
        </div>
      ),
      sortValue: (row) => Number(row?.offset_backward ?? -1),
    },
    {
      field: 'offset_forward',
      label: 'Forward Offset',
      align: 'center',
      flex: 0.3,
      minWidth: '70px',
      maxWidth: '70px',
      component: (row) => (
        <div className="font-averta text-xs font-bold text-primary-text">
          {getForwardOffsetLabel(row)}
        </div>
      ),
      sortValue: (row) => Number(row?.offset_forward ?? -1),
    },
    {
      field: 'status',
      label: 'Status',
      align: 'center',
      flex: 2,
      component: (row) => {
        const status = row?.status || '-';
        const active =
          String(status).toUpperCase() === String(statusActiveValue);
        return (
          <div className="flex items-center justify-center gap-2">
            <CustomSwitchV2
              checked={active}
              onChange={() => toggleStatus(row)}
              size="small"
              color="green"
            />
            <span className="text-xs">{status}</span>
          </div>
        );
      },
      sortValue: (row) => row?.status || '',
    },
    {
      field: 'actions',
      label: 'Actions',
      align: 'center',
      flex: 2,
      component: (row) => (
        <div className="flex items-center justify-center gap-2">
          <IconButtonV2
            noBorder
            icon="pen"
            aria-label="edit"
            title="Edit"
            onClick={() => openEdit(row)}
          />
          <IconButtonV2
            noBorder
            icon="deleteIcon"
            aria-label="delete"
            title="Delete"
            onClick={() => requestDelete(row?.id)}
          />
        </div>
      ),
      sortValue: () => '',
    },
  ];

  return (
    <PageContainerAB title={pageTitle} mainPage={false} minWidth={1100}>
      <div className="pb-6">
        <div className="bg-white border border-border-light-secondary rounded-xl px-4 py-4 mb-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="m-0 mt-1 text-sm text-secondary-text">
                Each row is one independent schedule. Priority and offset are
                defined for every schedule whether it is Continuous or Boost.
              </p>
            </div>
            <IconButton
              text="+ Add Schedule"
              onClick={openCreate}
              color="#131517"
              style={{
                background: '#F8B100',
                borderRadius: 20,
                minWidth: 140,
                fontWeight: 700,
                border: '1px solid #F8B100',
              }}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-6">
            <LoadingV2 />
          </div>
        ) : (
          <TableV3
            headers={columns}
            data={Array.isArray(schedules) ? schedules : []}
            minWidth="1200px"
            stickyHeader={true}
            appHeaderHeight={64}
          />
        )}
      </div>
      <Modal
        show={formOpen}
        setShow={(value) => {
          if (value === false) {
            setFormOpen(false);
            resetForm();
          }
        }}
        notClose={true}
        noX={true}
        submit={submitForm}
        submitText={null}
        cancelText="Cancel"
        buttonPosition="flex-end"
        loadingbutton={saving}
        icon="calendarCog"
        title={editingId ? 'Edit Schedule' : 'Create Schedule'}
        titleColor="#131517"
        description="Choose the schedule type and the form will switch to show only relevant fields."
        minWidth={900}
        maxHeight={900}>
        <div className="w-full pt-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Scheduler Name"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
            <div className="flex flex-col gap-2">
              <span className="font-averta text-sm font-bold text-primary-text block">
                Terminal
              </span>
              <span className="font-averta text-sm text-primary-text">
                {titleTerminalName || '-'}
              </span>
            </div>
          </div>

          <div className="mt-6">
            <div className="font-averta text-lg font-bold text-primary-text">
              Schedule Type
            </div>
            <div className="text-sm text-secondary-text mt-1">
              Each schedule can be either Continuous Monitoring or Boost Check.
            </div>
            <div className="mt-3 mb-5 max-w-sm">
              <CustomSelectEx
                dropdown
                height={48}
                maxHeight={48}
                title=""
                value={typeOptions.find((it) => it.value === form.type) || null}
                onChange={(item) =>
                  setForm((p) => ({ ...p, type: item?.value || 'CONTINUES' }))
                }
                options={typeOptions}
                placeholder="Select schedule type"
              />
            </div>
          </div>

          <div className="mt-2">
            <div className="font-averta text-lg font-bold text-primary-text">
              {form.type === 'CONTINUES'
                ? 'Continuous Monitoring Settings'
                : 'Boost Check Settings'}
            </div>
            <div className="text-sm text-secondary-text mt-1 mb-4">
              This schedule uses a monitoring window and repeats based on the
              selected frequency.
            </div>
            <div className="grid grid-cols-4 gap-3">
              <div className="col-span-2">
                <CustomSelectEx
                  dropdown
                  height={48}
                  maxHeight={48}
                  title="Days"
                  multi
                  value={selectedDaysValue}
                  onChange={(selected) => {
                    const selectedKeys = Array.isArray(selected)
                      ? selected.map((item) => item.value)
                      : [];
                    setForm((prev) => ({
                      ...prev,
                      day_mon: selectedKeys.includes('day_mon'),
                      day_tue: selectedKeys.includes('day_tue'),
                      day_wed: selectedKeys.includes('day_wed'),
                      day_thu: selectedKeys.includes('day_thu'),
                      day_fri: selectedKeys.includes('day_fri'),
                      day_sat: selectedKeys.includes('day_sat'),
                      day_sun: selectedKeys.includes('day_sun'),
                    }));
                  }}
                  options={dayOptions}
                  placeholder="Select days"
                />
              </div>
              <div
                className={
                  form.type === 'CONTINUES' ? 'col-span-1' : 'col-span-2'
                }>
                <span className="font-averta mb-2 text-sm font-bold text-primary-text block">
                  Start Time
                </span>
                <TimePickerV2
                  value={form.time_start}
                  inputHeightClassName="h-12 text-sm pr-8"
                  onChange={(value) =>
                    setForm((p) => ({ ...p, time_start: value }))
                  }
                />
              </div>
              {form.type === 'CONTINUES' && (
                <div className="col-span-1">
                  <span className="font-averta mb-2 text-sm font-bold text-primary-text block">
                    End Time
                  </span>
                  <TimePickerV2
                    value={form.time_end}
                    inputHeightClassName="h-12 text-sm pr-8"
                    onChange={(value) =>
                      setForm((p) => ({ ...p, time_end: value }))
                    }
                  />
                </div>
              )}
            </div>

            <div
              className={`grid gap-3 mt-4 ${
                form.type === 'CONTINUES' ? 'grid-cols-2' : 'grid-cols-1'
              }`}>
              {form.type === 'CONTINUES' && (
                <Input
                  type="number"
                  min="1"
                  label="Frequency (min)"
                  value={form.frequency}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, frequency: e.target.value }))
                  }
                />
              )}
              <CustomSelectEx
                dropdown
                height={48}
                maxHeight={48}
                title="Priority"
                value={
                  priorityOptions.find((it) => it.value === form.priority) ||
                  null
                }
                onChange={(item) =>
                  setForm((p) => ({ ...p, priority: item?.value || '' }))
                }
                options={priorityOptions}
                placeholder="Priority"
              />
            </div>
          </div>

          <div className="mt-6">
            <div className="font-averta text-lg font-bold text-primary-text">
              Check Time Offset
            </div>
            <div className="text-sm text-secondary-text mt-1 mb-4">
              Define a time range around the scheduled checks so they do not
              always happen at the exact same minute.
            </div>
            <div className="grid grid-cols-2 gap-3 mb-1">
              <Input
                type="number"
                min="0"
                label="Backward Offset"
                value={form.offset_backward}
                onChange={(e) =>
                  setForm((p) => ({ ...p, offset_backward: e.target.value }))
                }
              />
              <Input
                type="number"
                min="0"
                label="Forward Offset"
                value={form.offset_forward}
                onChange={(e) =>
                  setForm((p) => ({ ...p, offset_forward: e.target.value }))
                }
              />
            </div>
          </div>

          <div className="flex w-full justify-end items-center gap-4 mt-8 pt-4">
            <CloseButton
              onClick={() => {
                setFormOpen(false);
                resetForm();
              }}
              text="Cancel"
              width={120}
            />

            <SubmitButton
              text={editingId ? 'Update Schedule' : 'Save Schedule'}
              onClick={submitForm}
              disable={false}
              loading={saving}
            />
          </div>
        </div>
      </Modal>

      <DeletePopup
        show={deleteConfirm.show}
        handleDelete={() => performDelete(deleteConfirm.agendaId)}
        closePopup={() => setDeleteConfirm({ show: false, agendaId: null, loading: false })}
        loading={deleteConfirm.loading}
      />
    </PageContainerAB>
  );
}
